import React, { useState, useEffect } from 'react';
import styles from './MenuBar.module.css';
import { MACOS_MENUBAR_HEIGHT_PX } from '../../../constants';

/** Formats a Date as "Sat 22 Feb  9:41 AM" — the classic macOS clock format */
function formatClock(date: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = days[date.getDay()];
  const d = date.getDate();
  const mon = months[date.getMonth()];
  let h = date.getHours();
  const m = date.getMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${day} ${d} ${mon}  ${h}:${m} ${ampm}`;
}

const MENU_ITEMS = ['Finder', 'File', 'Edit', 'View', 'Go', 'Window', 'Help'];

export function MenuBar() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      className={styles.menuBar}
      style={{ height: MACOS_MENUBAR_HEIGHT_PX }}
      aria-label="macOS menu bar"
    >
      {/* ── Left: Apple logo + app menus ── */}
      <div className={styles.left}>
        {/* Apple  logo */}
        <button className={`${styles.item} ${styles.apple}`} aria-label="Apple menu" tabIndex={-1}>
          <AppleIcon />
        </button>

        {MENU_ITEMS.map((label, i) => (
          <button
            key={label}
            className={`${styles.item} ${i === 0 ? styles.appName : ''}`}
            tabIndex={-1}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Right: status icons + clock ── */}
      <div className={styles.right}>
        {/* Wifi icon */}
        <span className={styles.statusIcon} aria-hidden="true">
          <WifiIcon />
        </span>
        {/* Battery icon */}
        <span className={styles.statusIcon} aria-hidden="true">
          <BatteryIcon />
        </span>
        {/* Spotlight */}
        <span className={styles.statusIcon} aria-label="Spotlight">
          <SearchIcon />
        </span>
        <span className={styles.clock}>{formatClock(now)}</span>
      </div>
    </div>
  );
}

/* ─── Inline SVG icons ─────────────────────────────────────── */

function AppleIcon() {
  return (
    <svg width="13" height="14" viewBox="0 0 13 14" fill="currentColor">
      <path d="M10.53 7.44c-.02-1.98 1.62-2.93 1.69-2.98-.92-1.35-2.35-1.54-2.86-1.56-1.21-.12-2.37.71-2.99.71-.62 0-1.57-.7-2.58-.68-1.32.02-2.55.77-3.23 1.95-1.38 2.39-.35 5.92 1 7.86.66.96 1.45 2.03 2.49 1.99 1-.04 1.38-.65 2.59-.65 1.2 0 1.54.65 2.59.63 1.07-.02 1.75-.97 2.4-1.94.76-1.12 1.07-2.2 1.09-2.26-.02-.01-2.17-.84-2.19-3.07zM8.66 1.97c.55-.67.92-1.6.82-2.53-.79.03-1.75.53-2.32 1.19-.51.59-.96 1.54-.84 2.44.88.07 1.78-.44 2.34-1.1z" />
    </svg>
  );
}

function WifiIcon() {
  return (
    <svg width="15" height="11" viewBox="0 0 15 11" fill="currentColor">
      <path d="M7.5 8.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm0-3c1.25 0 2.37.51 3.18 1.33l.87-.87A5.47 5.47 0 0 0 7.5 4a5.47 5.47 0 0 0-3.88 1.6l.87.87C5.21 5.73 6.29 5.5 7.5 5.5zm0-3A8.48 8.48 0 0 1 13.57 5l.87-.87A9.72 9.72 0 0 0 7.5 2a9.72 9.72 0 0 0-6.97 2.97L1.44 5.84A8.47 8.47 0 0 1 7.5 2.5z" />
    </svg>
  );
}

function BatteryIcon() {
  return (
    <svg width="22" height="11" viewBox="0 0 22 11" fill="none">
      <rect x="0.5" y="0.5" width="18" height="10" rx="2.5" stroke="currentColor" strokeOpacity="0.5" />
      <rect x="2" y="2" width="13" height="7" rx="1.5" fill="currentColor" fillOpacity="0.8" />
      <path d="M19.5 3.5v4a1.5 1.5 0 0 0 0-4z" fill="currentColor" fillOpacity="0.4" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 9l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}
